/**
 * Reflects the records for saved carts in the DAM.
 * @class
 */
import AssetSetModel from '@netx/core-asset/models/assetSet';
import {types as assetSetTypes} from '@netx/core-asset/properties/assetSets';
import Base from '@netx/core-share/collections/sharedResources';
import Model from '../models/savedCart';

export default class SavedCarts extends Base {
	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/** @type {object} */
	get xhrLoadingMessages() {
		return {
			getSharedResourcesForUser() {
				return this.isFiltered()
					? 'Filtering saved carts'
					: 'Loading saved carts';
			},
			getAllCarts: 'Loading saved carts',
			getUserCarts: 'Loading saved carts',
		};
	}

	/** @type {object} */
	get xhrLoadedMessages() {
		return {
			getSharedResourcesForUser() {
				return this.isFiltered()
					? 'Filtered saved carts'
					: 'Loaded saved carts';
			},
			getAllCarts: 'Loaded saved carts',
			getUserCarts: 'Loaded saved carts',
		};
	}

	/**
	 * @property {object} methods                    - Methods for this collection
	 * @property {method} methods.fetchBeans         - Fetch saved cart beans
	 * @property {method} methods.fetchBeansForUser  - Fetch saved cart beans for user
	 */
	get methods() {
		return _.extend(super.methods, {
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			fetchBeans(opts) {
				return [
					'getAllCarts',
					this.modeFilters,
					this.filterStr,
					this.pager.getPageDTO(),
				];
			},
			/**
			 * @method
			 * @param {number} userId User id
			 * @param {object} opts Request options
			 */
			fetchBeansForUser(userId, opts) {
				return [
					'getUserCarts',
					userId,
					this.modeFilters,
					this.filterStr,
					this.pager.getPageDTO(),
				];
			},
		});
	}

	/**
	 * @property {object} parsers                    - Parsers for this collection
	 * @property {method} parsers.fetchBeans         - Parse response of saved cart beans
	 * @property {method} parsers.fetchBeansForUser  - Parse response of saved cart beans
	 */
	get parsers() {
		return _.extend(super.parsers, {
			/**
			 * @function
			 * @param {array} data Saved carts
			 * @param {object} opts Request options
			 */
			fetchBeans(data, opts = {}) {
				this.set(data, _.extend({merge: true}, opts));
				!opts.silent && this.trigger('sync', this, data, opts);
			},
			/**
			 * @function
			 * @param {array} data Saved carts
			 * @param {number} userId User id
			 * @param {object} opts Request options
			 */
			fetchBeansForUser(data, userId, opts = {}) {
				this.set(data, opts);
				!opts.silent && this.trigger('sync', this, data, opts);
			},
		});
	}

	/**
	 * @override
	 * @listens module:cart#cart:saved:delete:success
	 * @listens module:cart#cart:save:current:success
	 */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;
		opts.resourceName = 'savedCart';

		super(models, opts);

		// Set name filter if provided
		this.filterStr = opts.filterStr || this.filterStr;

		// Hey, listen!
		this.listenTo(dispatcher, 'cart:saved:delete:success', (savedCart) => {
			const model = this.get(savedCart.id);
			model && this.remove(model);
		}).listenTo(dispatcher, 'cart:save:current:success', (savedCart) => {
			this.matchesFilters(savedCart) && this.unshift(savedCart.attributes);
		});
	}

	/**
	 * @method
	 * @param {array} ids Saved cart ids
	 * @param {array} assetIds Asset ids
	 * @return {Promise -> Netx.Collections.SavedCarts}
	 */
	addAssets(ids, assetIds = []) {
		// Get all carts involved
		return this.getCarts(ids || []).then((savedCarts) => {
			// Add current cart assets and store the promises
			const addAssets = savedCarts.map((savedCart) =>
				savedCart.addAssetsToCart(assetIds),
			);
			// Wait for everything to finish
			return $.when.apply($, addAssets).then(() => {
				return savedCarts;
			});
		});
	}

	/**
	 * @method
	 * @param {array} ids Saved cart ids to update
	 * @return {Promise -> Netx.Collections.SavedCarts}
	 */
	addCurrentCart(ids) {
		if (!ids || !ids.length) return new $.Deferred().resolve([]);
		// Get all carts involved
		return this.getCarts(ids || []).then((savedCarts) => {
			// Add current cart assets and store the promises
			const addAssets = savedCarts.map((savedCart) =>
				savedCart.addCurrentCart(),
			);
			// Wait for everything to finish
			return Promise.all(addAssets).then(() => {
				return savedCarts;
			});
		});
	}

	/**
	 * @method
	 * @param {array} ids Saved cart ids
	 * @param {Netx.Collections.SearchRules} search Search rules
	 * @return {Promise -> Netx.Collections.SavedCarts}
	 */
	addSearch(ids, search) {
		// Get all carts involved
		return this.getCarts(ids || []).then((savedCarts) => {
			// Add current cart assets and store the promises
			const addAssets = savedCarts.map((savedCart) =>
				savedCart.addFacetedSearchToCart(search),
			);
			// Wait for everything to finish
			return $.when.apply($, addAssets).then(() => {
				return savedCarts;
			});
		});
	}

	/**
	 * @method
	 * @param {array} carts An array of cart objects `{title: <title>}`
	 * @param {object} opts Options
	 */
	createNewCarts(carts, opts = {}) {
		return new Promise((resolve, reject) => {
			if (!carts) resolve([]);

			const newCarts = [];
			// Save each cart after the other has completed to avoid data locks on the server
			const next = () => {
				if (!carts.length) return resolve(newCarts);

				const cart = new Model(carts.shift());
				cart.save(opts).then(() => {
					newCarts.push(cart);
					next();
				}, reject);
			};

			next();
		});
	}

	/**
	 * @method
	 * @param {number} id Cart id
	 * @return {module:cart/models/savedCart}
	 */
	getCart(id) {
		let savedCart = this.get(id);
		if (savedCart) {
			return new $.Deferred().resolve(savedCart);
		}

		return new Model({
			[Model.prototype.idAttribute || 'id']: id,
		})
			.fetch()
			.then(() => {
				return savedCart;
			});
	}

	/**
	 * @method
	 * @param {array} ids Cart ids
	 * @return {Netx.Collections.SavedCarts}
	 */
	getCarts(ids) {
		// Get cart data for any cartIds passed
		const carts = _.compact(
			_.map(ids, (id) => (id && this.getCart(id)) || null),
		);
		// Wait for all cart models to be available
		return $.when.apply($, carts).then((...carts) => {
			return new this.constructor([...carts]);
		});
	}

	/**
	 * Get all carts that current user can edit
	 * @method
	 * @return {Netx.Collections.SavedCarts}
	 * @todo this will not work once we have paging enforced - this will need to fetch and return a promise
	 * but the actions need to be reworked to allow for promises in enable tests
	 */
	getEditableCarts() {
		return new this.constructor(this.filter((cart) => cart.canEdit()));
	}

	/**
	 * Check if we have any carts the current user can edit
	 * @method
	 */
	hasEditableCarts() {
		return this.getEditableCarts().length > 0;
	}

	/**
	 * @method
	 * @return {module:asset/models/assetSet} AssetSet
	 */
	toAssetSet() {
		return new AssetSetModel({
			type: assetSetTypes.cart,
			ids: this.ids(),
			label: i18n.t('i18n.savedCart'),
		});
	}

	/** @type {string} */
	static get __name__() {
		return 'SavedCarts';
	}
}
